import React, {
	useEffect,
	useRef,
	useState,
	lazy,
	useCallback,
	Suspense,
} from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useDispatch, useSelector } from "react-redux";
import {
	loginSuccess,
	logout,
} from "./redux/userRoute/authenticate/authenticate.action";
import "react-toastify/dist/ReactToastify.css";
import { Drawer } from "@material-tailwind/react";
import Setting from "./components/Setting";
import { setScrollToDown } from "./redux/userRoute/authenticate/authenticate.slicer";
import Loader from "./pages/Loader";
import ReactGA from "react-ga4";

// lazy loading
const Home = lazy(() => import("./pages/Home"));
const Viste = lazy(() => import("./pages/Viste"));
const CheckIn = lazy(() => import("./pages/CheckIn"));
const GuestShop = lazy(() => import("./pages/GuestShop"));
const Alfredin = lazy(() => import("./pages/Alfredin"));
const SignUp = lazy(() => import("./pages/SignUp"));
const PoliceVerification = lazy(() => import("./pages/PoliceVerification"));
const Error = lazy(() => import("./pages/Error"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyMail = lazy(() => import("./pages/VerifyMail"));
const LockScreen = lazy(() => import("./pages/LockScreen"));
const Report = lazy(() => import("./pages/Report"));
const Human = lazy(() => import("./pages/Human"));
const Map = lazy(() => import("./pages/Map"));
const OrderFood = lazy(() => import("./pages/OrderFood"));
const Feedback = lazy(() => import("./pages/Feedback"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Love = lazy(() => import("./pages/Love"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const WhatsTermsAndConditions = lazy(() =>
	import("./pages/WhatsTermsAndConditions.js"),
);
const PrivacyPolicy = lazy(() => import("./pages/Privacy"));

const ScrollToTop = () => {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	return null;
};

const App = () => {
	const [isSettingOpen, setIsSettingOpen] = useState(true);
	const [showInactivityScreen, setShowInactivityScreen] = useState(false);
	const inactivityTimeoutRef = useRef(null);
	const logoutTimeoutRef = useRef(null);
	const dispatch = useDispatch();
	const { isAuthenticated, isUser, formSkipped } = useSelector(
		(state) => state.authenticate,
	);
	const pathname = window.location.pathname;
	const disabledRoutes = [
		"/helpdesk",
		"/discover",
		"/v360",
		"/feedback",
		"/book",
		"/report",
	];

	const isTimeoutEnabled = !disabledRoutes.includes(pathname);

	useEffect(() => {
		ReactGA.initialize("G-EPLE8W211T");
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			// microsoft clarity
			(function (c, l, a, r, i, t, y) {
				c[a] =
					c[a] ||
					function () {
						(c[a].q = c[a].q || []).push(arguments);
					};
				t = l.createElement(r);
				t.async = 1;
				t.src = "https://www.clarity.ms/tag/" + i;
				y = l.getElementsByTagName(r)[0];
				y.parentNode.insertBefore(t, y);
			})(window, document, "clarity", "script", "odc174c7td");

			ReactGA.event({
				category: "User Engagement",
				action: "User Logged In",
				label: "User Login",
				value: 1,
				nonInteraction: false,
			});
		}
	}, [isAuthenticated]);

	useEffect(() => {
		dispatch(loginSuccess());

		return () => {
			ReactGA.event({
				category: "User Engagement",
				action: "Session End",
				label: "Window closed",
				value: 1,
				nonInteraction: false,
			});
		};
	}, [dispatch]);

	const resetInactivityTimeout = () => {
		if (isTimeoutEnabled) {
			clearTimeout(inactivityTimeoutRef.current);
			inactivityTimeoutRef.current = setTimeout(() => {
				setShowInactivityScreen(true);
			}, 5 * 60 * 1000);
		}
	};

	const resetLogoutTimeout = () => {
		if (isTimeoutEnabled) {
			clearTimeout(logoutTimeoutRef.current);
			logoutTimeoutRef.current = setTimeout(() => {
				if (isAuthenticated) {
					logoutHandler();
				}
			}, 24 * 60 * 60 * 1000);
		}
	};

	const handleActivity = () => {
		if (isTimeoutEnabled) {
			resetInactivityTimeout();
			resetLogoutTimeout();
		}
	};

	const handleReactivate = useCallback(() => {
		setShowInactivityScreen(false);
		resetInactivityTimeout();
		resetLogoutTimeout();
		dispatch(setScrollToDown(true));
	}, [showInactivityScreen]); // eslint-disable-line

	useEffect(() => {
		if (isTimeoutEnabled) {
			resetInactivityTimeout();
			resetLogoutTimeout();

			window.addEventListener("mousemove", handleActivity);
			window.addEventListener("keydown", handleActivity);
			window.addEventListener("scroll", handleActivity);
			window.addEventListener("click", handleActivity);

			return () => {
				clearTimeout(inactivityTimeoutRef.current);
				clearTimeout(logoutTimeoutRef.current);
				window.removeEventListener("mousemove", handleActivity);
				window.removeEventListener("keydown", handleActivity);
				window.removeEventListener("scroll", handleActivity);
				window.removeEventListener("click", handleActivity);
			};
		}
	}, [isTimeoutEnabled]); //eslint-disable-line

	const logoutHandler = async () => {
		dispatch(logout());
		ReactGA.event({
			category: "User Engagement",
			action: "User Logged Out",
			label: "User Out",
			value: 1,
			nonInteraction: false,
		});
		setIsSettingOpen(false);
	};

	return (
		<BrowserRouter>
			{showInactivityScreen ? (
				<LockScreen onReactivate={handleReactivate} />
			) : (
				<>
					<ScrollToTop />
					{isAuthenticated &&
						(isUser.policeFormSubmitted || formSkipped) && (
							<Drawer
								transition={{ duration: 0.1 }}
								open={isSettingOpen}
								onClose={() => setIsSettingOpen(false)}
								size={300}>
								<Setting
									logoutHandler={logoutHandler}
									setIsSettingOpen={setIsSettingOpen}
								/>
							</Drawer>
						)}
					<Routes>
						<Route
							path="/"
							element={
								<Suspense fallback={<Loader />}>
									<Home
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
										user={isUser}
										formSkipped={formSkipped}
									/>
								</Suspense>
							}
						/>
						<Route path="/terms" element={<TermsAndConditions />} />
						<Route
							path="/freshlanding/terms"
							element={
								<Suspense fallback={<Loader />}>
									<WhatsTermsAndConditions />
								</Suspense>
							}
						/>
						<Route
							path="/privacy"
							element={
								<Suspense fallback={<Loader />}>
									<PrivacyPolicy />
								</Suspense>
							}
						/>
						<Route
							path="/signup"
							element={
								<Suspense fallback={<Loader />}>
									<SignUp isAuthenticated={isAuthenticated} />
								</Suspense>
							}
						/>
						<Route
							path="/police/verification"
							element={
								<Suspense fallback={<Loader />}>
									<PoliceVerification
										isAuthenticated={isAuthenticated}
										user={isUser}
										formSkipped={formSkipped}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/password/forgot"
							element={
								<Suspense fallback={<Loader />}>
									<ForgotPassword
										isAuthenticated={isAuthenticated}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/password/reset/:token"
							element={
								<Suspense fallback={<Loader />}>
									<ResetPassword
										isAuthenticated={isAuthenticated}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/mail/verify/:token"
							element={
								<Suspense fallback={<Loader />}>
									<VerifyMail />
								</Suspense>
							}
						/>
						<Route
							path="/loved"
							element={
								<Suspense fallback={<Loader />}>
									<Love
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/viste"
							element={
								<Suspense fallback={<Loader />}>
									<Viste
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/checkin"
							element={
								<Suspense fallback={<Loader />}>
									<CheckIn
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
										user={isUser}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/guestshop/chat"
							element={
								<Suspense fallback={<Loader />}>
									<GuestShop
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>

						<Route
							path="/guestshop/order/food"
							element={
								<Suspense fallback={<Loader />}>
									<OrderFood
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/guestshop"
							element={
								<Suspense fallback={<Loader />}>
									<Alfredin
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/feedback"
							element={
								<Suspense fallback={<Loader />}>
									<Feedback
										user={isUser}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/reportissue"
							element={
								<Suspense fallback={<Loader />}>
									<Report
										user={isUser}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/pathfinder"
							element={
								<Suspense fallback={<Loader />}>
									<Map
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/helpdesk"
							element={
								<Suspense fallback={<Loader />}>
									<Human
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/faq"
							element={
								<Suspense fallback={<Loader />}>
									<FAQ
										language={isUser?.lang}
										logoutHandler={logoutHandler}
										setIsSettingOpen={setIsSettingOpen}
										isAuthenticated={isAuthenticated}
										isSettingOpen={isSettingOpen}
									/>
								</Suspense>
							}
						/>
						<Route
							path="/*"
							element={
								<Suspense fallback={<Loader />}>
									<Error />
								</Suspense>
							}
						/>
					</Routes>
				</>
			)}
		</BrowserRouter>
	);
};

export default App;
